import React, {useEffect, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {ProjectIndicator} from "./mobile-common";

export default function MobileQuickCreate({visible, onHide, types = [], loading, error, onSubmit, onTypeChange, translate}) {
    const [selectedType, setSelectedType] = useState("task");
    const [title, setTitle] = useState("");
    const [destination, setDestination] = useState(null);
    const submitLockRef = useRef(false);

    useEffect(() => {
        if (!visible) return;
        const initialType = types.find((item) => !item.disabled)?.value || types[0]?.value || "task";
        setSelectedType(initialType);
        setTitle("");
        setDestination(null);
        submitLockRef.current = false;
        // Reset only when the sheet opens; parent loading/state updates must not erase the form.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const selectedOption = types.find((item) => item.value === selectedType);
    const destinations = selectedOption?.destinations || [];
    const requiresDestination = selectedType !== "project";
    const unavailable = Boolean(selectedOption?.disabled || (requiresDestination && !destinations.length));

    const valid = Boolean(title.trim() && (!requiresDestination || destination) && !unavailable);
    const submit = async (event) => {
        event.preventDefault();
        if (!valid || loading || submitLockRef.current) return;
        submitLockRef.current = true;
        try {
            await onSubmit({type: selectedType, title: title.trim(), destination: requiresDestination ? destination : null});
        } finally {
            submitLockRef.current = false;
        }
    };

    const selectType = (type) => {
        if (loading || type.disabled) return;
        setSelectedType(type.value);
        setDestination(null);
        onTypeChange?.(type.value);
    };

    const destinationItemTemplate = (option) => option ? (
        <div className="flex align-items-center gap-2 py-1 w-full min-w-0">
            {option.type === "project"
                ? <ProjectIndicator project={option.item}/>
                : <span className="w-2rem h-2rem flex align-items-center justify-content-center mobile-brand-radius surface-100 text-600"><i className={`pi ${option.type === "workspace" ? "pi-building" : "pi-check-circle"}`}/></span>}
            <span className="flex-1 min-w-0 text-xs text-overflow-ellipsis overflow-hidden white-space-nowrap">{option.label}</span>
        </div>
    ) : <span className="text-500 text-xs">{translate("mobile.quick-create.destination-placeholder")}</span>;

    const destinationValueTemplate = (option) => option ? (
        <div className="flex align-items-center gap-2 w-full min-w-0 line-height-1">
            <i className={`pi ${option.type === "project" ? "pi-folder" : option.type === "workspace" ? "pi-building" : "pi-check-circle"} text-xs mobile-brand-text flex-shrink-0`}/>
            <span className="flex-1 min-w-0 text-xs text-700 text-overflow-ellipsis overflow-hidden white-space-nowrap">{option.label}</span>
        </div>
    ) : <span className="text-500 text-xs">{translate("mobile.quick-create.destination-placeholder")}</span>;

    const destinationFilterTemplate = (options) => (
        <IconField dir="rtl" className="w-full mobile-quick-create-filter-field">
            <InputIcon className="pi pi-search text-500"/>
            <InputText
                className="w-full text-xs py-2 mobile-quick-create-filter-input"
                placeholder={options.props.filterPlaceholder}
                onChange={options.filterOptions.filter}
            />
        </IconField>
    );

    return (
        <Dialog
            visible={visible}
            position="bottom"
            modal
            dismissableMask
            blockScroll
            closeOnEscape
            draggable={false}
            showHeader={false}
            className="shabnam-font rtl w-full m-0 mobile-brand-radius-top"
            contentClassName="p-0 mobile-brand-radius-top"
            onHide={() => !loading && onHide()}
        >
            <div className="safe-area-bottom flex flex-column">
                <div className="flex justify-content-center pt-2 pb-1">
                    <span className="surface-200 border-round-xl" style={{width: "2.75rem", height: ".22rem"}}/>
                </div>
                <div className="flex align-items-center justify-content-between px-3 pb-2">
                    <h2 className="text-lg m-0">{translate("mobile.quick-create.title")}</h2>
                    <Button type="button" text rounded className="w-2rem h-2rem p-0 surface-100 text-500 " icon="pi pi-times text-xs" aria-label={translate("cancel")} disabled={loading} onClick={onHide}/>
                </div>
                <form className="flex flex-column gap-3 px-3 pb-4" onSubmit={submit}>
                    <div className="grid m-0" role="tablist" aria-label={translate("mobile.quick-create.type-label")}>
                        {types.map((type) => {
                            const selected = selectedType === type.value;
                            return (
                                <div className="col-4 p-1" key={type.value}>
                                    <button
                                        data-testid={`quick-create-type-${type.value}`}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        disabled={type.disabled || loading}
                                        className={`w-full flex flex-column align-items-center justify-content-center gap-2 border-1 text-700 cursor-pointer ${selected ? "mobile-brand-bg-soft mobile-brand-border text-900" : "surface-card border-200"} ${type.disabled ? "p-disabled" : ""}`}
                                        style={{minHeight: "5.5rem", borderRadius: "14px"}}
                                        onClick={() => selectType(type)}
                                    >
                                        <i className={`${type.icon} text-xl`}/>
                                        <strong className="text-sm">{type.label}</strong>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-2xs text-500 text-center line-height-3 m-0 px-2">
                        {translate(`mobile.quick-create.${selectedType}-description`)}
                    </p>
                    {error && <Message data-testid="quick-create-error" className="text-xs" severity="error" text={error}/>} 
                    {!error && unavailable && <Message data-testid="quick-create-error" className="text-xs" severity="warn" text={translate("mobile.quick-create.type-no-permission")}/>} 
                    <QuickField required label={translate("mobile.quick-create.content-label")}>
                        <InputText
                            data-testid="quick-create-title"
                            autoFocus
                            className="w-full min-h-3rem text-sm mobile-brand-radius"
                            value={title}
                            placeholder={translate("mobile.quick-create.content-placeholder")}
                            disabled={loading}
                            onChange={(event) => setTitle(event.target.value)}
                        />
                    </QuickField>
                    <div className="mobile-quick-create-destination-slot">
                        {requiresDestination ? (
                            <QuickField required label={translate("mobile.quick-create.destination-label")}>
                                <Dropdown
                                    data-testid="quick-create-destination"
                                    className="mobile-quick-create-dropdown w-full h-3rem text-xs mobile-brand-radius"
                                    panelClassName="rtl shabnam-font mobile-quick-create-dropdown-panel"
                                    options={destinations}
                                    optionLabel="label"
                                    value={destination}
                                    filter
                                    filterBy="label"
                                    filterPlaceholder={translate("mobile.quick-create.destination-search")}
                                    filterTemplate={destinationFilterTemplate}
                                    placeholder={translate("mobile.quick-create.destination-placeholder")}
                                    itemTemplate={destinationItemTemplate}
                                    valueTemplate={destinationValueTemplate}
                                    disabled={loading || unavailable}
                                    onChange={(event) => setDestination(event.value)}
                                />
                            </QuickField>
                        ) : (
                            <div data-testid="quick-create-destination-spacer" className="mobile-quick-create-destination-spacer" aria-hidden="true">
                                <QuickField label={translate("mobile.quick-create.destination-label")}>
                                    <div className="w-full h-3rem"/>
                                </QuickField>
                            </div>
                        )}
                    </div>
                    <Button data-testid="quick-create-submit" type="submit" className="w-full min-h-3rem justify-content-center gap-2 mobile-brand-radius mt-1" icon="pi pi-plus" label={translate("mobile.quick-create.submit")} loading={loading} disabled={!valid || loading}/>
                </form>
            </div>
        </Dialog>
    );
}

function QuickField({label, required = false, children}) {
    return <label className="flex flex-column gap-2 text-xs text-700 font-bold"><span>{label}{required && <span className="text-red-500 mr-1">*</span>}</span>{children}</label>;
}
